import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import type { Transcription } from '../../../../entities';
import SortByMenu from '../../../molecules/SortByMenu';
import Songlist from './Songlist';
import { TrackingAction, TrackingCategory, trackingService } from '../../../../services';

export enum SortOption {
  ByTitle = 'home.songbook.sortby.sort_by_title',
  ByCreationDate = 'home.songbook.sortby.sort_by_creation_date',
}

const useStyles = makeStyles()((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2em',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '4.5em',
    },
  },

  topContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },

  searchField: {
    margin: '1rem 0',
    flex: '1 1 20rem',
  },

  listContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

export interface SongbookProps {
  transcriptions?: Transcription[];
}

/**
 * Component representing a users' songbook, with list of transcriptions, searching,
 * options for downloading, deleting, editing, opening
 */
const Songbook: React.FC<SongbookProps> = ({ transcriptions = [] }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [sortOption, setSortOption] = useState<SortOption>(SortOption.ByCreationDate);
  const [sortAscending, setSortAscending] = useState({
    [SortOption.ByTitle]: true,
    [SortOption.ByCreationDate]: false,
  });
  const onAscendingChange = useCallback(
    (value: boolean) => {
      setSortAscending((prevState) => ({
        ...prevState,
        [sortOption]: value,
      }));
    },
    [sortOption, setSortAscending],
  );

  const [filterString, setFilterString] = useState<string>('');

  const sortOptions = useMemo(
    // @ts-ignore
    () => Object.keys(SortOption).map((k) => ({ key: SortOption[k], label: t(SortOption[k]) })),
    [t],
  );

  return (
    <div className={classes.container} data-testid="songbook">
      {transcriptions.length > 1 && (
        <div className={classes.topContainer}>
          <TextField
            onFocus={() =>
              trackingService.event(
                TrackingCategory.Transcription,
                TrackingAction.SONGBOOK_SEARCH_USED,
              )}
            className={classes.searchField}
            placeholder={t('home.songbook.search_placeholder')}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment:
                filterString !== '' ? (
                  <IconButton onClick={() => setFilterString('')} size="large">
                    <Clear />
                  </IconButton>
                ) : (
                  ''
                ),
            }}
            onKeyDown={(e) => (e.key === 'Esc' ? setFilterString('') : '')}
            value={filterString}
            onChange={(e) => setFilterString(e.target.value)}
          />

          <SortByMenu
            options={sortOptions}
            selectedOption={sortOption}
            onSelectedOptionChange={(option) => setSortOption(option)}
            ascending={sortAscending[sortOption]}
            onAscendingChange={onAscendingChange}
            disabled={filterString !== ''}
          />
        </div>
      )}

      <div className={classes.listContainer}>
        <Songlist
          transcriptions={transcriptions}
          searchString={filterString}
          sortOption={sortOption}
          sortAscending={sortAscending[sortOption]}
        />
      </div>
    </div>
  );
};

export default Songbook;
